(function attachAstroAspectPatterns(root, factory) {
  const api = factory();

  if (typeof module === "object" && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.AstroAspectPatterns = api;
  }
})(typeof globalThis !== "undefined" ? globalThis : this, function createAstroAspectPatterns() {
  "use strict";

  const EPSILON = 1e-9;
  const DEFAULT_PATTERN_PLANETS = Object.freeze([
    "Sun",
    "Moon",
    "Mercury",
    "Venus",
    "Mars",
    "Jupiter",
    "Saturn",
    "Uranus",
    "Neptune",
    "Pluto"
  ]);
  const PATTERN_ORB_CAPS = Object.freeze({
    60: 4,
    90: 6,
    120: 6,
    150: 3,
    180: 6
  });
  const PATTERN_ANGLES = Object.freeze(Object.keys(PATTERN_ORB_CAPS).map(Number));
  const PATTERN_DEFINITIONS = Object.freeze([
    Object.freeze({
      type: "grand_cross",
      label: "Gran Cruz",
      description: "Dos oposiciones se entrelazan mediante cuatro cuadraturas.",
      size: 4,
      signature: Object.freeze([90, 90, 90, 90, 180, 180]),
      priority: 60
    }),
    Object.freeze({
      type: "kite",
      label: "Cometa",
      description: "Un Gran Trino se articula con una oposición y dos sextiles; también se conoce como Cometa cósmica.",
      size: 4,
      signature: Object.freeze([60, 60, 120, 120, 120, 180]),
      priority: 50
    }),
    Object.freeze({
      type: "mystic_rectangle",
      label: "Rectángulo Místico",
      description: "Dos oposiciones quedan conectadas por dos trígonos y dos sextiles.",
      size: 4,
      signature: Object.freeze([60, 60, 120, 120, 180, 180]),
      priority: 40
    }),
    Object.freeze({
      type: "t_square",
      label: "T-cuadrada",
      description: "Una oposición descarga su tensión en un tercer planeta mediante dos cuadraturas.",
      size: 3,
      signature: Object.freeze([90, 90, 180]),
      priority: 30
    }),
    Object.freeze({
      type: "yod",
      label: "Yod · Dedo de Dios",
      description: "Dos planetas en sextil convergen por quincuncios en un planeta ápice.",
      size: 3,
      signature: Object.freeze([60, 150, 150]),
      priority: 20
    }),
    Object.freeze({
      type: "grand_trine",
      label: "Gran Trino",
      description: "Tres planetas enlazados por trígonos forman un circuito armónico cerrado.",
      size: 3,
      signature: Object.freeze([120, 120, 120]),
      priority: 10
    })
  ]);
  const DEFINITION_BY_TYPE = new Map(PATTERN_DEFINITIONS.map((definition) => [definition.type, definition]));

  function normalizeLongitude(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === "string" && !value.trim()) return null;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    return ((numeric % 360) + 360) % 360;
  }

  function angularDistance(longitudeA, longitudeB) {
    const a = normalizeLongitude(longitudeA);
    const b = normalizeLongitude(longitudeB);
    if (a === null || b === null) return Number.NaN;
    const distance = Math.abs(a - b);
    return Math.min(distance, 360 - distance);
  }

  function round(value, decimals = 6) {
    const factor = 10 ** decimals;
    return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
  }

  function configuredOrb(orbs, angle) {
    const configured = orbs instanceof Map ? orbs.get(angle) : orbs?.[angle];
    const numeric = Number(configured);
    if (!Number.isFinite(numeric)) return PATTERN_ORB_CAPS[angle];
    return Math.min(PATTERN_ORB_CAPS[angle], Math.max(0, numeric));
  }

  function normalizePlanets(planets, requestedKeys) {
    const allowedKeys = new Set(
      Array.isArray(requestedKeys) && requestedKeys.length
        ? requestedKeys.map(String)
        : DEFAULT_PATTERN_PLANETS
    );
    const normalized = new Map();

    for (const planet of Array.isArray(planets) ? planets : []) {
      const key = String(planet?.key || "").trim();
      if (!key || !allowedKeys.has(key) || normalized.has(key)) continue;
      const longitude = normalizeLongitude(planet?.longitude);
      if (longitude === null) continue;
      normalized.set(key, { key, longitude });
    }

    return Array.from(normalized.values()).sort((planetA, planetB) => planetA.key.localeCompare(planetB.key));
  }

  function canonicalPair(planetA, planetB) {
    return String(planetA).localeCompare(String(planetB)) <= 0
      ? [String(planetA), String(planetB)]
      : [String(planetB), String(planetA)];
  }

  function edgeKey(planetA, planetB) {
    return canonicalPair(planetA, planetB).join("|");
  }

  function buildAspectEdges(planets, orbs) {
    const edges = [];

    for (let index = 0; index < planets.length; index += 1) {
      for (let nextIndex = index + 1; nextIndex < planets.length; nextIndex += 1) {
        const source = planets[index];
        const target = planets[nextIndex];
        const separation = angularDistance(source.longitude, target.longitude);
        const matches = PATTERN_ANGLES
          .map((angle) => ({
            angle,
            orb: Math.abs(separation - angle),
            orbLimit: configuredOrb(orbs, angle)
          }))
          .filter((match) => match.orb <= match.orbLimit + EPSILON)
          .sort((matchA, matchB) => matchA.orb - matchB.orb || matchA.angle - matchB.angle);
        const match = matches[0];
        if (!match) continue;

        const [planetA, planetB] = canonicalPair(source.key, target.key);
        edges.push({
          planetA,
          planetB,
          angle: match.angle,
          orb: round(match.orb),
          separation: round(separation),
          dissociated: isDissociatedAspect(source.longitude, target.longitude, match.angle)
        });
      }
    }

    return edges;
  }

  function isDissociatedAspect(longitudeA, longitudeB, angle) {
    const signA = Math.floor(normalizeLongitude(longitudeA) / 30);
    const signB = Math.floor(normalizeLongitude(longitudeB) / 30);
    const rawSignDistance = Math.abs(signA - signB);
    const signDistance = Math.min(rawSignDistance, 12 - rawSignDistance);
    return signDistance !== angle / 30;
  }

  function combinations(items, size) {
    const result = [];

    function visit(startIndex, selection) {
      if (selection.length === size) {
        result.push(selection.slice());
        return;
      }

      for (let index = startIndex; index <= items.length - (size - selection.length); index += 1) {
        selection.push(items[index]);
        visit(index + 1, selection);
        selection.pop();
      }
    }

    visit(0, []);
    return result;
  }

  function edgesForPlanets(keys, edgeLookup) {
    const edges = [];
    for (let index = 0; index < keys.length; index += 1) {
      for (let nextIndex = index + 1; nextIndex < keys.length; nextIndex += 1) {
        const edge = edgeLookup.get(edgeKey(keys[index], keys[nextIndex]));
        if (!edge) return null;
        edges.push(edge);
      }
    }
    return edges;
  }

  function sameSignature(edges, signature) {
    if (!edges || edges.length !== signature.length) return false;
    const actual = edges.map((edge) => edge.angle).sort((angleA, angleB) => angleA - angleB);
    return signature.every((angle, index) => actual[index] === angle);
  }

  function edgesAtAngle(edges, angle) {
    return edges.filter((edge) => edge.angle === angle);
  }

  function remainingPlanet(planets, edge) {
    return planets.find((key) => key !== edge.planetA && key !== edge.planetB) || null;
  }

  function kiteRoles(planets, edges) {
    const opposition = edgesAtAngle(edges, 180)[0];
    const sextiles = edgesAtAngle(edges, 60);
    const focus = [opposition.planetA, opposition.planetB].find((key) => (
      sextiles.filter((edge) => edge.planetA === key || edge.planetB === key).length === 2
    ));
    const opposite = focus === opposition.planetA ? opposition.planetB : opposition.planetA;
    const wings = planets.filter((key) => key !== focus && key !== opposite).sort();
    return {
      focus,
      opposite,
      wings,
      axis: [opposite, focus],
      grandTrine: [opposite, ...wings].sort()
    };
  }

  function rolesForPattern(type, planets, edges) {
    if (type === "t_square") {
      const opposition = edgesAtAngle(edges, 180)[0];
      return {
        apex: remainingPlanet(planets, opposition),
        opposition: [opposition.planetA, opposition.planetB]
      };
    }

    if (type === "yod") {
      const base = edgesAtAngle(edges, 60)[0];
      return {
        apex: remainingPlanet(planets, base),
        base: [base.planetA, base.planetB]
      };
    }

    if (type === "grand_cross") {
      return {
        oppositions: edgesAtAngle(edges, 180).map((edge) => [edge.planetA, edge.planetB]),
        squares: edgesAtAngle(edges, 90).map((edge) => [edge.planetA, edge.planetB])
      };
    }

    if (type === "kite") return kiteRoles(planets, edges);

    if (type === "mystic_rectangle") {
      return {
        oppositions: edgesAtAngle(edges, 180).map((edge) => [edge.planetA, edge.planetB]),
        trines: edgesAtAngle(edges, 120).map((edge) => [edge.planetA, edge.planetB]),
        sextiles: edgesAtAngle(edges, 60).map((edge) => [edge.planetA, edge.planetB])
      };
    }

    return { vertices: planets.slice() };
  }

  function publicEdge(edge) {
    return {
      planetA: edge.planetA,
      planetB: edge.planetB,
      angle: edge.angle,
      orb: edge.orb
    };
  }

  function createPattern(definition, planets, edges) {
    const sortedPlanets = planets.slice().sort();
    const sortedEdges = edges.slice().sort((edgeA, edgeB) => (
      edgeA.angle - edgeB.angle
      || edgeA.planetA.localeCompare(edgeB.planetA)
      || edgeA.planetB.localeCompare(edgeB.planetB)
    ));
    return {
      id: `${definition.type}:${sortedPlanets.join("|")}`,
      type: definition.type,
      label: definition.label,
      description: definition.description,
      planets: sortedPlanets,
      edges: sortedEdges.map(publicEdge),
      roles: rolesForPattern(definition.type, sortedPlanets, sortedEdges),
      maxOrb: round(Math.max(...sortedEdges.map((edge) => edge.orb))),
      contains: [],
      dissociated: sortedEdges.some((edge) => edge.dissociated)
    };
  }

  function isSubset(subset, superset) {
    const parent = new Set(superset);
    return subset.every((item) => parent.has(item));
  }

  function attachContainedPatterns(patterns) {
    const kites = patterns.filter((pattern) => pattern.type === "kite");
    const grandCrosses = patterns.filter((pattern) => pattern.type === "grand_cross");

    for (const parent of [...kites, ...grandCrosses]) {
      const containedType = parent.type === "kite" ? "grand_trine" : "t_square";
      const contained = patterns.filter((candidate) => (
        candidate.type === containedType && isSubset(candidate.planets, parent.planets)
      ));
      parent.contains = contained.map((candidate) => ({
        id: candidate.id,
        type: candidate.type,
        label: candidate.label,
        planets: candidate.planets.slice()
      }));
    }

    return patterns;
  }

  function suppressContainedPatterns(patterns) {
    const containedIds = new Set(
      patterns.flatMap((pattern) => pattern.contains.map((contained) => contained.id))
    );
    return patterns.filter((pattern) => !containedIds.has(pattern.id));
  }

  function sortPatterns(patterns) {
    return patterns.sort((patternA, patternB) => {
      const priorityA = DEFINITION_BY_TYPE.get(patternA.type)?.priority || 0;
      const priorityB = DEFINITION_BY_TYPE.get(patternB.type)?.priority || 0;
      return priorityB - priorityA
        || patternA.maxOrb - patternB.maxOrb
        || patternA.id.localeCompare(patternB.id);
    });
  }

  function detectAspectPatterns(planets, options = {}) {
    const normalizedPlanets = normalizePlanets(planets, options.planetKeys);
    if (normalizedPlanets.length < 3) return [];

    const edges = buildAspectEdges(normalizedPlanets, options.orbs || {});
    const edgeLookup = new Map(edges.map((edge) => [edgeKey(edge.planetA, edge.planetB), edge]));
    const detectedById = new Map();

    for (const definition of PATTERN_DEFINITIONS) {
      const groups = combinations(normalizedPlanets.map((planet) => planet.key), definition.size);
      for (const group of groups) {
        const groupEdges = edgesForPlanets(group, edgeLookup);
        if (!sameSignature(groupEdges, definition.signature)) continue;
        const pattern = createPattern(definition, group, groupEdges);
        detectedById.set(pattern.id, pattern);
      }
    }

    const withContainment = attachContainedPatterns(Array.from(detectedById.values()));
    const visiblePatterns = options.suppressContained === false
      ? withContainment
      : suppressContainedPatterns(withContainment);
    return sortPatterns(visiblePatterns);
  }

  return Object.freeze({
    DEFAULT_PATTERN_PLANETS,
    PATTERN_DEFINITIONS,
    PATTERN_ORB_CAPS,
    detectAspectPatterns
  });
});
